/** @fileoverview Manages screen navigation state and sidebar targets. */

import { useEffect, useRef, useState } from 'react';
import { isRecord } from '../../lib/utils';
import type {
  AppActionWrite,
  FabrikAppDefinition,
  FabrikInputValue,
  FabrikTarget,
  InputSnapshot,
} from '../../types/apps';
import { odataSourcesOf } from './fieldOptions';
import type { FieldValues, ScreenBlock } from './types';
import { readHashScreenId, useAppShellNavigation } from './AppFrame';

export function useScreenNavigation(definition: FabrikAppDefinition) {
  const { selectedScreenId, navigationVersion, selectScreen } = useAppShellNavigation();
  const [screenBlocks, setScreenBlocks] = useState<ScreenBlock[]>(() => {
    /* A standard page screen named in the address opens first, even one without a tab. */
    const hashScreenId = readHashScreenId();
    const firstScreen =
      definition.screens.find(
        ({ id, kind, displayType }) =>
          id === hashScreenId && kind === 'standard' && displayType !== 'sidebar'
      ) ??
      definition.screens.find((screen) => screen.id === selectedScreenId) ??
      definition.screens.find((screen) => screen.id === definition.navigation?.screens[0]) ??
      definition.screens[0];
    return firstScreen ? [{ screenId: firstScreen.id, fields: {} }] : [];
  });
  const [inputs, setInputs] = useState<InputSnapshot>({});
  /* The tab choice the first screen already reflects, so the first run keeps a linked screen. */
  const shownChoiceRef = useRef({ selectedScreenId, navigationVersion });
  useEffect(() => {
    const shown = shownChoiceRef.current;
    if (
      shown.selectedScreenId === selectedScreenId &&
      shown.navigationVersion === navigationVersion
    )
      return;
    shownChoiceRef.current = { selectedScreenId, navigationVersion };
    /* The frame picks its first tab after the screens load; that is not a choice of the viewer. */
    if (shown.selectedScreenId === null) return;
    if (!selectedScreenId || !definition.navigation?.screens.includes(selectedScreenId)) return;
    setScreenBlocks((current) =>
      current.length === 1 && current[0].screenId === selectedScreenId && !current[0].sidebar
        ? current
        : [{ screenId: selectedScreenId, fields: {} }]
    );
  }, [definition.navigation?.screens, navigationVersion, selectedScreenId]);
  const commitStandaloneInput = (inputId: string, value: FabrikInputValue | undefined) =>
    setInputs((current) => {
      const standalone = { ...current.standalone };
      if (value === undefined) delete standalone[inputId];
      else standalone[inputId] = value;
      return { ...current, standalone };
    });

  const openTarget = (
    target: FabrikTarget,
    values: FieldValues = {},
    sourceItemId?: string,
    sourceScreenId?: string
  ) => {
    if (target.type !== 'screen') return;
    const targetScreen = definition.screens.find((candidate) => candidate.id === target.screenId);
    if (!targetScreen) return;
    if (
      targetScreen.kind === 'standard' &&
      definition.navigation?.screens.includes(targetScreen.id)
    )
      selectScreen(targetScreen.id);
    if (targetScreen.displayType === 'sidebar') {
      setScreenBlocks((current) =>
        current.map((block, index) =>
          index === current.length - 1
            ? {
                ...block,
                sidebar: {
                  screenId: targetScreen.id,
                  fields: values,
                  open: !(
                    block.sidebar?.open &&
                    block.sidebar.screenId === targetScreen.id &&
                    block.sidebar.sourceScreenId === sourceScreenId &&
                    block.sidebar.sourceItemId === sourceItemId
                  ),
                  sourceScreenId,
                  sourceItemId,
                },
              }
            : block
        )
      );
      return;
    }
    setScreenBlocks((current) => {
      const existingIndex = current.findIndex((block) => block.screenId === targetScreen.id);
      if (existingIndex >= 0) {
        const next = current.slice(0, existingIndex + 1);
        next[existingIndex] = { ...next[existingIndex], fields: values, sidebar: undefined };
        return next;
      }
      return [...current, { screenId: targetScreen.id, fields: values }];
    });
  };

  const goBack = () => setScreenBlocks((current) => current.slice(0, -1));
  /**
   * Returns to an earlier screen in the open trail, closing every screen opened after it.
   * @param blockIndex position of the screen to return to, 0 for the first
   */
  const goToBlock = (blockIndex: number) =>
    setScreenBlocks((current) => current.slice(0, blockIndex + 1));

  const closeSidebar = () =>
    setScreenBlocks((current) =>
      current.map((entry, index) =>
        index === current.length - 1 && entry.sidebar
          ? { ...entry, sidebar: { ...entry.sidebar, open: false } }
          : entry
      )
    );

  const finishSidebarClose = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (
      event.target !== event.currentTarget ||
      screenBlocks[screenBlocks.length - 1]?.sidebar?.open
    )
      return;
    setScreenBlocks((current) =>
      current.map((entry, index) =>
        index === current.length - 1 && entry.sidebar && !entry.sidebar.open
          ? { ...entry, sidebar: undefined }
          : entry
      )
    );
  };

  /**
   * Puts the values a write sent into every open screen that shows that record: a row read from
   * a data source of the written entity whose key fields hold the written keys. The screens then
   * show what was saved without reading it again.
   * @param writes the write outcomes of an action run
   */
  const applyWrites = (writes: AppActionWrite[]) => {
    const entityBySource = new Map(
      odataSourcesOf(definition).map(({ id, entity }) => [id, entity])
    );
    const patch = (fields: FieldValues): FieldValues =>
      Object.fromEntries(
        Object.entries(fields).map(([sourceId, row]) => {
          const written = writes.find(
            ({ entity, record }) =>
              record &&
              isRecord(row) &&
              entityBySource.get(sourceId) === entity &&
              record.keys.every((key) => String(row[key]) === String(record.values[key]))
          );
          return [
            sourceId,
            written?.record && isRecord(row) ? { ...row, ...written.record.values } : row,
          ];
        })
      );
    setScreenBlocks((current) =>
      current.map((block) => ({
        ...block,
        fields: patch(block.fields),
        ...(block.sidebar
          ? { sidebar: { ...block.sidebar, fields: patch(block.sidebar.fields) } }
          : {}),
      }))
    );
  };

  return {
    screenBlocks,
    applyWrites,
    inputs,
    setInputs,
    commitStandaloneInput,
    openTarget,
    goBack,
    goToBlock,
    closeSidebar,
    finishSidebarClose,
  };
}
